"use client";

import React, { Suspense } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import Link from 'next/link';
import { ArrowRight } from 'lucide-react';
import Image from 'next/image';
import Layout from '@/components/Layout';
import { PageHero } from '@/components/sections/PageHero';
import NicheTag from '@/components/NicheTag';
import JsonLd from '@/components/SEO/JsonLd';
import { portfolioProjects } from '@/data/siteData';

const filters = ['all', 'nonprofit', 'government', 'hoa', 'church', 'small-business'] as const;

const filterLabels: Record<string, string> = {
  all: 'All',
  nonprofit: 'Nonprofit',
  government: 'Government',
  hoa: 'HOA',
  church: 'Church',
  'small-business': 'Small Business',
};

function PortfolioContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const activeFilter = searchParams.get('niche') || 'all';

  const filtered = activeFilter === 'all' 
    ? portfolioProjects 
    : portfolioProjects.filter((p) => p.orgType === activeFilter);

  const setFilter = (f: string) => {
    const params = new URLSearchParams(searchParams.toString());
    if (f === 'all') {
      params.delete('niche');
    } else {
      params.set('niche', f);
    }
    router.push(`/portfolio?${params.toString()}`);
  };

  return (
    <>
      <PageHero
        title="Work built for missions that matter."
        subtitle="Accessible, high-performance digital transformations for community-focused organizations."
        ariaLabel="Portfolio header"
      />

      <section className="bg-bloom-cream py-16" aria-labelledby="projects-list-heading">
        <div className="container mx-auto px-6">
          <h2 id="projects-list-heading" className="sr-only">Our Projects</h2>
          
          <div
            role="group"
            aria-label="Filter projects by organization type"
            className="flex flex-wrap gap-3 mb-4"
          >
            {filters.map((f) => (
              <button
                key={f}
                aria-pressed={activeFilter === f}
                onClick={() => setFilter(f)}
                className={`px-5 py-2 rounded-full font-sans text-sm font-medium transition-colors focus-bloom ${
                  activeFilter === f ? 'bg-bloom-green-deep text-primary-foreground' : 'bg-bloom-cream-dark text-bloom-charcoal hover:bg-bloom-linen'
                }`}
              >
                {filterLabels[f] ?? f}
              </button>
            ))}
          </div>

          <p
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="font-sans text-sm text-bloom-muted mb-12"
          >
            Showing {filtered.length} project{filtered.length !== 1 ? 's' : ''}
          </p>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {filtered.map((p, index) => {
              const CardContent = (
                <>
                  <div className="aspect-video bg-muted relative overflow-hidden group-hover:scale-105 transition-transform duration-500">
                    <Image 
                      src={p.heroImage} 
                      alt="" 
                      fill
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      className="object-cover"
                      aria-hidden="true"
                      priority={index < 2}
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <NicheTag niche={p.orgType} />
                      <span className="font-sans text-xs text-bloom-muted">{p.complianceLevel}</span>
                    </div>
                    <h3 className="font-serif text-xl font-bold text-bloom-charcoal mb-2">{p.name}</h3>
                    <p className="font-sans text-sm text-bloom-muted mb-3">{p.tagline}</p>
                    <p className="font-sans text-xs text-bloom-green-mid font-medium mb-4">{p.impactStatement}</p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {p.techStack.map((t) => (
                        <span key={t} className="px-2.5 py-1 rounded bg-bloom-linen text-bloom-charcoal font-sans text-xs">{t}</span>
                      ))}
                    </div>
                    {!p.isMock && !p.caseStudyPending && (
                      <span className="font-sans text-sm font-medium text-bloom-green-mid group-hover:underline inline-flex items-center gap-1">
                        View Case Study <ArrowRight size={14} aria-hidden="true" />
                      </span>
                    )}
                    {p.isMock && (
                      <span className="font-sans text-[10px] uppercase tracking-widest font-bold text-bloom-gold-dark mt-2 block">
                        Design Concept
                      </span>
                    )}
                    {p.caseStudyPending && (
                      <span className="font-sans text-[10px] uppercase tracking-widest font-bold text-bloom-muted mt-2 block">
                        Case Study Coming Soon
                      </span>
                    )}
                  </div>
                </>
              );

              if (p.isMock || p.caseStudyPending) {
                return (
                  <div 
                    key={p.slug}
                    className="group bg-card rounded-2xl overflow-hidden block border border-bloom-border transition-all shadow-sm cursor-default"
                  >
                    {CardContent}
                  </div>
                );
              }

              return (
                <Link 
                  key={p.slug} 
                  href={`/portfolio/${p.slug}`} 
                  className="group bg-card rounded-2xl overflow-hidden card-hover block focus-bloom border border-bloom-border hover:border-bloom-green-light transition-all shadow-sm"
                  aria-label={`View case study: ${p.name}`}
                >
                  {CardContent}
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}

export default function PortfolioPage() {
  return (
    <Layout>
      <Suspense fallback={<div className="min-h-screen bg-bloom-cream animate-pulse" />}>
        <PortfolioContent />
      </Suspense>
    </Layout>
  );
}
